/**
 * 뷰 아틀라스 — N장의 뷰를 텍스처 한 장에 격자로 담는다.
 *
 * 셰이더가 뷰 여덟 장을 각각 텍스처로 받는 건 비싸고(샘플러 슬롯), glTF 로 내보낼
 * 때도 한 장이 낫다. 각 뷰를 실루엣 bbox 로 잘라 같은 높이로 맞춰 칸에 넣고,
 * 칸의 UV 사각형을 기록한다. 셰이더는 (s,t) 를 그 사각형 안으로 옮겨 샘플링한다.
 *
 * 크기 상한 4096 — 모바일 GPU 의 일반적인 MAX_TEXTURE_SIZE. 8뷰면 칸 하나 1024×1024 정도.
 */

import type { Field, Raster } from '../types.ts';
import { encodeSrgb, linearize, makeField, makeRaster } from '../imaging/raster.ts';
import type { TurnaroundSet, ViewImage } from './views.ts';

export interface AtlasRect {
  /** (s,t) → uv = (u0 + s·du, v0 + t·dv). v 는 위가 1. */
  u0: number;
  v0: number;
  du: number;
  dv: number;
}

export interface ViewAtlas {
  raster: Raster;
  mask: Field;
  rects: AtlasRect[];
  cellHeight: number;
  /** 뷰별 노출 보정 이득(정면 = 1). 디버그 표시용. */
  gains: number[];
}

/** 마스크 안쪽의 선형 휘도 평균. */
function meanLinearLuma(r: Raster, mask: Field): number {
  let sum = 0, n = 0;
  for (let i = 0, p = 0; i < mask.data.length; i++, p += 4) {
    if (mask.data[i]! < 0.5) continue;
    sum += 0.2126 * linearize(r.data[p]!) + 0.7152 * linearize(r.data[p + 1]!) + 0.0722 * linearize(r.data[p + 2]!);
    n++;
  }
  return n ? sum / n : 0;
}

/**
 * 뷰 사이 노출 맞추기. 생성 모델은 장마다 조명을 조금씩 다르게 그린다 — 후면이 정면보다
 * 한 단 어둡거나 하는 식. 그대로 투영하면 뷰 경계가 밝기 띠로 선다. 뷰마다 마스크 안
 * 평균 휘도를 정면에 맞추는 스칼라 이득(선형 공간, ±15% 상한)을 건다. 색조는 건드리지
 * 않는다 — 후면의 색이 다른 건 캐릭터가 그런 것이다.
 */
function exposureGains(views: ViewImage[]): number[] {
  const lumas = views.map((v) => meanLinearLuma(v.raster, v.mask));
  const refIdx = Math.max(0, views.findIndex((v) => v.azimuth === 0));
  const ref = lumas[refIdx]! || 1e-3;
  return lumas.map((l) => (l > 1e-4 ? Math.min(1.15, Math.max(0.87, ref / l)) : 1));
}

function applyGain(dst: Raster, dstMask: Field, x0: number, y0: number, w: number, h: number, gain: number) {
  if (Math.abs(gain - 1) < 0.005) return;
  for (let y = y0; y < y0 + h; y++) {
    for (let x = x0; x < x0 + w; x++) {
      const i = y * dst.width + x;
      if (dstMask.data[i]! < 0.02) continue;
      const p = i * 4;
      for (let c = 0; c < 3; c++) dst.data[p + c] = encodeSrgb(linearize(dst.data[p + c]!) * gain);
    }
  }
}

/** 쌍선형 리샘플로 src 의 사각형을 dst 의 사각형에 그린다. 마스크도 같이. */
function blit(
  src: Raster, srcMask: Field, sx0: number, sy0: number, sw: number, sh: number,
  dst: Raster, dstMask: Field, dx0: number, dy0: number, dw: number, dh: number,
) {
  const sxScale = sw / dw;
  const syScale = sh / dh;
  for (let y = 0; y < dh; y++) {
    const fy = sy0 + (y + 0.5) * syScale - 0.5;
    const y0 = Math.max(0, Math.min(src.height - 1, Math.floor(fy)));
    const y1 = Math.min(src.height - 1, y0 + 1);
    const ty = Math.max(0, Math.min(1, fy - y0));
    for (let x = 0; x < dw; x++) {
      const fx = sx0 + (x + 0.5) * sxScale - 0.5;
      const x0 = Math.max(0, Math.min(src.width - 1, Math.floor(fx)));
      const x1 = Math.min(src.width - 1, x0 + 1);
      const tx = Math.max(0, Math.min(1, fx - x0));
      const di = ((dy0 + y) * dst.width + (dx0 + x));
      const p00 = (y0 * src.width + x0) * 4, p10 = (y0 * src.width + x1) * 4;
      const p01 = (y1 * src.width + x0) * 4, p11 = (y1 * src.width + x1) * 4;
      for (let c = 0; c < 4; c++) {
        const a = src.data[p00 + c]! + (src.data[p10 + c]! - src.data[p00 + c]!) * tx;
        const b = src.data[p01 + c]! + (src.data[p11 + c]! - src.data[p01 + c]!) * tx;
        dst.data[di * 4 + c] = a + (b - a) * ty;
      }
      const m00 = srcMask.data[y0 * src.width + x0]!, m10 = srcMask.data[y0 * src.width + x1]!;
      const m01 = srcMask.data[y1 * src.width + x0]!, m11 = srcMask.data[y1 * src.width + x1]!;
      const ma = m00 + (m10 - m00) * tx, mb = m01 + (m11 - m01) * tx;
      dstMask.data[di] = ma + (mb - ma) * ty;
    }
  }
}

export function buildAtlas(set: TurnaroundSet, maxSize = 4096): ViewAtlas {
  const views = set.views;
  const n = views.length;
  const cols = n <= 3 ? n : Math.ceil(Math.sqrt(n));
  const rows = Math.ceil(n / cols);
  const maxAspect = Math.max(...views.map((v) => v.aspect), 0.2);

  // 칸 높이: 원본 bbox 높이의 최댓값을 따르되 아틀라스가 상한을 넘지 않게.
  const tallest = Math.max(...views.map((v) => v.bbox.y1 - v.bbox.y0));
  let cellH = Math.min(1024, tallest);
  let cellW = Math.ceil(cellH * maxAspect) + 8;
  const fit = Math.min(maxSize / (cols * cellW), maxSize / (rows * (cellH + 8)));
  if (fit < 1) { cellH = Math.floor(cellH * fit); cellW = Math.ceil(cellH * maxAspect) + 8; }
  const W = cols * cellW;
  const H = rows * (cellH + 8);

  const raster = makeRaster(W, H);
  // 배경은 흰색 — 뷰 경계에서 새는 색이 있어도 튀지 않게.
  raster.data.fill(255);
  const mask = makeField(W, H);
  const rects: AtlasRect[] = [];
  const gains = exposureGains(views);

  views.forEach((v: ViewImage, i) => {
    const col = i % cols, row = Math.floor(i / cols);
    const bw = Math.max(1, v.bbox.x1 - v.bbox.x0);
    const bh = Math.max(1, v.bbox.y1 - v.bbox.y0);
    const dw = Math.max(1, Math.round(cellH * (bw / bh)));
    const dh = cellH;
    const dx0 = col * cellW + 4;
    const dy0 = row * (cellH + 8) + 4;
    blit(v.raster, v.mask, v.bbox.x0, v.bbox.y0, bw, bh, raster, mask, dx0, dy0, dw, dh);
    applyGain(raster, mask, dx0, dy0, dw, dh, gains[i]!);
    rects.push({ u0: dx0 / W, v0: 1 - (dy0 + dh) / H, du: dw / W, dv: dh / H });
  });

  return { raster, mask, rects, cellHeight: cellH, gains };
}
